/* eslint-disable no-param-reassign */
import { api } from "jiffy-api";
import { isElementVisible } from "common/utils/ui/visibility";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";

const initialState = {
  $fileInput: null,
  $modal: null,
  designs: {
    continuation: undefined,
    items: []
  },
  error: null,
  intervalId: null,
  loading: false,
  settings: {},
  uploading: false
};

const canvaModule = {
  namespaced: true,

  state() {
    return { ...initialState };
  },

  getters: {
    $fileInput: state => state.$fileInput,
    $modal: state => state.$modal,
    continuation: state => state.designs.continuation,
    designs: state => state.designs.items,
    error: state => state.error,
    intervalId: state => state.intervalId,
    loading: state => state.loading,
    settings: state => state.settings,
    uploading: state => state.uploading
  },

  mutations: {
    $setFileInput(state, $fileInput) {
      state.$fileInput = $fileInput;
    },
    $setModal(state, $modal) {
      state.$modal = $modal;
    },
    setError(state, error) {
      state.error = "Something went wrong, please try again.";

      try {
        const match = error.match(/\{.*\}/);
        const jsonError = JSON.parse(match[0]);
        state.error = `Error: ${jsonError.message}`;
      } catch (_) {
        console.error(error);
      }
    },
    setDesigns(state, designs) {
      state.designs = {
        continuation: designs.continuation,
        items: [...state.designs.items, ...designs.items]
      };
    },
    clearDesigns(state) {
      state.designs = {
        continuation: undefined,
        items: []
      };
    },
    clearError(state) {
      state.error = null;
    },
    setIntervalId(state, id) {
      state.intervalId = id;
    },
    setSettings(state, settings) {
      state.settings = settings;
    },
    toggleLoading(state, force) {
      state.loading = force;
    },
    toggleUploading(state, force) {
      state.uploading = force;
    }
  },

  actions: {
    async checkDesignStatus({ commit, dispatch, getters }, jobId) {
      const modalVisible = isElementVisible(getters.$modal);
      if (!modalVisible) {
        commit("toggleUploading", false);
        clearInterval(getters.intervalId);
        return;
      }

      try {
        const { job } = await api.canva.designStatus({ jobId });

        if (job.status === "success" && modalVisible) {
          clearInterval(getters.intervalId);
          await dispatch("setImageFromURL", job.urls[0]);
        } else if (!modalVisible) {
          clearInterval(getters.intervalId);
        }
      } catch (e) {
        commit("setError", e.message);
      } finally {
        commit("toggleUploading", false);
      }
    },
    async fetchDesigns({ commit }, continuation) {
      commit("toggleLoading", true);

      try {
        const params = continuation ? { continuation } : {};
        const designs = await api.canva.designs(params);
        commit("setDesigns", designs);
      } catch (e) {
        /*
         * 401 is returned when the token is invalid or expired
         * We need to reload the page to get a new token
         */
        if (e.status === 401) {
          window.location.reload();
          return;
        }

        commit("setError", e.message);
      } finally {
        commit("toggleLoading", false);
      }
    },
    async fetchSettings({ commit }, namespace) {
      try {
        const settings = await api.canva.settings({
          source: namespace,
          redirect_url: window.location.pathname
        });
        commit("setSettings", settings);
      } catch (e) {
        commit("setError", e.message);
      }
    },
    async selectDesign({ commit, dispatch }, designId) {
      commit("toggleUploading", true);

      submitDtfEvent("canva_design_selected", { design_id: designId });

      try {
        const { job } = await api.canva.selectDesign({ designId });
        const intervalId = setInterval(() => {
          dispatch("checkDesignStatus", job.id);
        }, 1000);
        commit("setIntervalId", intervalId);
      } catch (e) {
        commit("setError", e.message);
        commit("toggleUploading", false);
      }
    },
    async setImageFromURL({ commit, getters }, url) {
      try {
        const response = await fetch(url);
        const blob = await response.blob();

        const changeEvent = new Event("change");
        const urlPath = new URL(url).pathname;
        const fileName = urlPath.substring(urlPath.lastIndexOf("/") + 1);
        const file = new File([blob], fileName, { type: blob.type });
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        getters.$fileInput.files = dataTransfer.files;
        getters.$fileInput.dispatchEvent(changeEvent);
        getters.$fileInput.setAttribute("data-upload-source", "canva");

        jQuery(getters.$modal).modal("hide");
      } catch (e) {
        commit("setError", e.message);
      }
    },
    resetModalState({ commit, dispatch, getters }) {
      commit("toggleUploading", false);
      commit("toggleLoading", false);

      if (getters.error) {
        commit("clearError");
        commit("clearDesigns");
        dispatch("fetchDesigns");
      }
    }
  }
};

export default canvaModule;
